import { createAction, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { cloneDeep, isNumber } from "lodash";
import { Currency } from "../../currency/objects/currency";
import { DestinationReduced } from "../../destination/objects/destinationReduced";
import { Picture } from "../../picture/objects/picture";
import { ADD_CUSTOM_PICTURES, SET_PICTURE } from "../../picture/redux/actionTypes";
import { Preferences } from "../../profile/objects/preferences";
import { DEFAULT_COLUMN } from "../constant/itineraryConstants";
import { ADULT_TRAVELER_FOR_GROUP, CHILD_TRAVELER_FOR_GROUP } from "../constant/travelerGroup";
import { Column } from "../objects/column";
import { Error } from "../objects/error";
import { ItineraryType } from "../objects/itineraryType";
import { ItineraryTypeState } from "../objects/itineraryTypeState";
import { List } from "../objects/list";
import { CircuitCategory } from "../../circuitCategory/objects/circuitCategory";


const domain = window.location.host.split('.')[1];

const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');
const setPictures = createAction<boolean | Picture>(SET_PICTURE);
const addPictures = createAction<Picture[]>(ADD_CUSTOM_PICTURES);

const initialState: ItineraryTypeState = {
    budget: '',
    columns: DEFAULT_COLUMN,
    count: 0,
    create: false,
    currency: null,
    currency_error: '',
    description: '',
    destination: null,
    destination_error: '',
    duration_error: '',
    edit: null,
    end_date: null,
    export_full: false,
    export_list: false,
    export_type: 'CSV',
    filter_open: false,
    filter_destination: null,
    filter_created_date_gte: null,
    filter_created_date_lte: null,
    filter_creator: '',
    filter_last_author: '',
    filter_modified_date_gte: null,
    filter_modified_date_lte: null,
    filter_period: 'CREATION',
    filter_period_type: 0,
    filter_visibility: null,
    itinerary_type: null,
    itinerary_type_with_products: false,
    itinerary_type_duplicate: [],
    list: null,
    limit: 50,
    name: '',
    name_error: '',
    video_link: '',
    order: 'asc',
    order_by: 'name',
    page: 0,
    picture: null,
    pictures: [],
    pictures_order: [],
    preferences: [],
    refresh: false,
    running: false,
    search: '',
    start_date: null,
    start_date_error: '',
    rooms_for_traveler: [0],
    adult_travelers: [ADULT_TRAVELER_FOR_GROUP, { ...ADULT_TRAVELER_FOR_GROUP, id: 1 }],
    child_travelers: [],
    categories:[],
    filter_categories: []
};

const initPicturesReducer = (state: ItineraryTypeState): ItineraryTypeState => ({
    ...state, pictures: []
});

const addItineraryTypeDuplicateReducer = (state: ItineraryTypeState, action: PayloadAction<ItineraryType & {copy:string}>): ItineraryTypeState => ({
	...state, itinerary_type_duplicate: state.itinerary_type_duplicate.find(item => item.id === action.payload.id) ? state.itinerary_type_duplicate.filter(item => item.id !== action.payload.id) : [...state.itinerary_type_duplicate, {...action.payload, name: `${action.payload.name} ${action.payload.copy}`  }]
});

const addAdultTravelersReducer = (state: ItineraryTypeState, action: PayloadAction<number>): ItineraryTypeState => ({
    ...state, adult_travelers: [...state.adult_travelers, { ...ADULT_TRAVELER_FOR_GROUP, id: state.adult_travelers.length, room_id: action.payload }]
});

const addRoomForTravelersReducer = (state: ItineraryTypeState): ItineraryTypeState => ({
    ...state,
    rooms_for_traveler: [...state.rooms_for_traveler, state.rooms_for_traveler[state.rooms_for_traveler.length - 1] + 1],
    adult_travelers: [...state.adult_travelers, { ...ADULT_TRAVELER_FOR_GROUP, room_id: state.rooms_for_traveler[state.rooms_for_traveler.length - 1] + 1 }],
});

const deleteRoomForTravelersReducer = (state: ItineraryTypeState, action: PayloadAction<number>): ItineraryTypeState => ({
    ...state,
    rooms_for_traveler: state.rooms_for_traveler.filter(room_id => room_id !== action.payload),
    adult_travelers: state.adult_travelers.filter(traveler => traveler.room_id !== action.payload),
    child_travelers: state.child_travelers.filter(traveler => traveler.room_id !== action.payload)
});

const deleteAdultTravelersReducer = (state: ItineraryTypeState, action: PayloadAction<number>): ItineraryTypeState => {
    let removed = false;
    const adult_travelers = cloneDeep(state.adult_travelers).reverse().filter((traveler) => {
        if (!removed && traveler.room_id === action.payload) {
            removed = true;
            return false;
        }
        return true;
    }).reverse();
    return {
        ...state, adult_travelers
    };
};

const addChildTravelersReducer = (state: ItineraryTypeState, action: PayloadAction<number>): ItineraryTypeState => ({
    ...state, child_travelers: [...state.child_travelers, { ...CHILD_TRAVELER_FOR_GROUP, id: state.child_travelers.length, room_id: action.payload }]
});

const updateChildTravelersReducer = (state: ItineraryTypeState, action: PayloadAction<{ child_id: number, age: number }>): ItineraryTypeState => ({
    ...state, child_travelers: state.child_travelers.map((child) => child.id === action.payload.child_id ? { ...child, birth_date: action.payload.age } : child)
});

const updateChildTravelersErrorReducer = (state: ItineraryTypeState, action: PayloadAction<{ child_id: number, error: string }>): ItineraryTypeState => ({
    ...state, child_travelers: state.child_travelers.map((child) => child.id === action.payload.child_id ? { ...child, error: action.payload.error } : child)
});

const deleteChildTravelersReducer = (state: ItineraryTypeState, action: PayloadAction<number>): ItineraryTypeState => {
    let removed = false;
    const child_travelers = cloneDeep(state.child_travelers).reverse().filter((traveler) => {
        if (!removed && traveler.room_id === action.payload) {
            removed = true;
            return false;
        }
        return true;
    }).reverse();
    return {
        ...state, child_travelers
    };
};


const addPreferencesReducer = (state: ItineraryTypeState, action: PayloadAction<Preferences[]>): ItineraryTypeState => ({
    ...state, preferences: [...state.preferences, ...action.payload]
});


const deletePictureReducer = (state: ItineraryTypeState): ItineraryTypeState => ({
    ...state, picture: null
});

const deletePicturesReducer = (
    state: ItineraryTypeState,
    action: PayloadAction<number>
): ItineraryTypeState => ({
    ...state,
    pictures: state.pictures.filter(
        (element, index) => index !== action.payload
    )
});

const addPicturesReducer = (
    state: ItineraryTypeState,
    action: PayloadAction<any>
): ItineraryTypeState => ({
    ...state, pictures: [...state.pictures, ...action.payload]
});

const setPicturesReducer = (
    state: ItineraryTypeState,
    action: PayloadAction<any>
): ItineraryTypeState =>
    !action.payload
        ? {
            ...state,
            pictures: state.pictures.map(
                (picture) =>
                    picture.id === (action.payload as Picture).id
                        ? (action.payload as Picture)
                        : picture
            )
        }
        : { ...state };


const moveDownColumnReducer = (state: ItineraryTypeState, action: PayloadAction<number>): ItineraryTypeState => {
    const columns = cloneDeep([...state.columns]);
    for (let i = 0; i < columns.length; i++) {
        columns[i].last_updated = false;
    }
    const column = { ...columns[action.payload + 1] };
    columns[action.payload + 1] = columns[action.payload];
    columns[action.payload + 1].last_updated = true;
    columns[action.payload] = column;
    return { ...state, columns };
};

const moveUpColumnReducer = (state: ItineraryTypeState, action: PayloadAction<number>): ItineraryTypeState => {
    const columns = cloneDeep([...state.columns]);
    for (let i = 0; i < columns.length; i++) {
        columns[i].last_updated = false;
    }
    const column = { ...columns[action.payload - 1] };
    columns[action.payload - 1] = columns[action.payload];
    columns[action.payload - 1].last_updated = true;
    columns[action.payload] = column;
    return { ...state, columns };
};

const resetFiltersReducer = (state: ItineraryTypeState): ItineraryTypeState => ({
    ...state,
    filter_created_date_gte: null,
    filter_created_date_lte: null,
    filter_creator: '',
    filter_destination: null,
    filter_categories: [],
    filter_last_author: '',
    filter_modified_date_gte: null,
    filter_modified_date_lte: null,
    filter_period: 'CREATION',
    filter_period_type: 0,
    filter_visibility: null,
});

const resetItineraryTypeReducer = (state: ItineraryTypeState): ItineraryTypeState => ({
    ...state,
    create: false,
    currency: null,
    currency_error: '',
    description: '',
    destination: null,
    categories: [],
    destination_error: '',
    end_date: null,
    duration_error: '',
    itinerary_type_with_products: false,
    name: '',
    video_link: '',
    name_error: '',
    picture: null,
    pictures: [],
    start_date: null,
    rooms_for_traveler: [0],
    adult_travelers: [ADULT_TRAVELER_FOR_GROUP, { ...ADULT_TRAVELER_FOR_GROUP, id: 1 }],
    child_travelers: [],
    edit: null
});

const reseteItineraryTypeDuplicateReducer = (state: ItineraryTypeState): ItineraryTypeState => ({
    ...state, itinerary_type_duplicate: []
});

const setEditReducer = (state: ItineraryTypeState, action: PayloadAction<null | 'INFO' | 'PICTURE' | 'PICTURES_ORDER' | 'DESCRIPTION' | 'VIDEO'>): ItineraryTypeState => ({
	...state,
	edit: action.payload,
	name: state.itinerary_type?.name || '',
	video_link: state.itinerary_type?.video_link || '',
	description: state.itinerary_type?.description || '',
    categories: state.itinerary_type?.categories || [],
	name_error: '',
    destination: state.itinerary_type?.data[0].destination as unknown as DestinationReduced || null,
    destination_error: '',
	budget: state.itinerary_type?.data[0]?.budget || '',
	picture: isNumber(state.itinerary_type?.picture)? null: state.itinerary_type?.picture ||null,
	pictures: state.itinerary_type?.pictures as Picture[] || [],
	pictures_order: state.itinerary_type && state.itinerary_type.pictures !== null
		? [...state.itinerary_type.pictures as Picture[]]
		: [],
});

const updatePicturesOrderReducer = (
    state: ItineraryTypeState,
    action: PayloadAction<{ drag_index: number; hover_index: number }>
): ItineraryTypeState => ({
    ...state,
    pictures_order: state.pictures_order.map((item, index) => {
        if (index === action.payload.drag_index) {
            return state.pictures_order[action.payload.hover_index];
        } else if (index === action.payload.hover_index) {
            return state.pictures_order[action.payload.drag_index];
        }
        return item;
    })
});


const setErrorsReducer = (
    state: ItineraryTypeState,
    action: PayloadAction<Error[]>
): ItineraryTypeState => {
    const errors = action.payload;
    const new_state: ItineraryTypeState = { ...state };
    errors.map((error) => {
        (new_state[error.key]) = error.error as never;
    });
    return { ...new_state };
};

const setExportTypeReducer = (state: ItineraryTypeState, action: PayloadAction<'CSV' | 'PDF'>): ItineraryTypeState => ({
    ...state, export_type: action.payload
});

const setItineraryTypeReducer = (state: ItineraryTypeState, action: PayloadAction<ItineraryType | null>): ItineraryTypeState => ({
    ...state, itinerary_type: action.payload ? {...action.payload, video_link: action.payload?.video_link || ''}: null
});

const setItineraryTypePreferencesReducer = (state: ItineraryTypeState, action: PayloadAction<Preferences[]>): ItineraryTypeState => ({
    ...state, preferences: action.payload
});

const setLimitReducer = (state: ItineraryTypeState, action: PayloadAction<number>): ItineraryTypeState => ({
    ...state, list: null, limit: action.payload
});

const setListReducer = (state: ItineraryTypeState, action: PayloadAction<List>): ItineraryTypeState => ({
    ...state, list: action.payload.results, count: action.payload.count, running: false
});

const setOrderByReducer = (state: ItineraryTypeState, action: PayloadAction<string>): ItineraryTypeState => ({
    ...state, list: null, order_by: action.payload, order: state.order_by === action.payload && state.order === 'asc' ? 'desc' : 'asc'
});

const setPageReducer = (state: ItineraryTypeState, action: PayloadAction<number>): ItineraryTypeState => ({
    ...state, page: action.payload, list: null
});

const setRunningReducer = (state: ItineraryTypeState, action: PayloadAction<boolean>): ItineraryTypeState => ({
    ...state, running: action.payload
});

const setSearchReducer = (state: ItineraryTypeState, action: PayloadAction<string>): ItineraryTypeState => ({
    ...state, list: null, page: 0, search: action.payload
});

const toggleColumnReducer = (state: ItineraryTypeState, action: PayloadAction<number>): ItineraryTypeState => {
    const columns = cloneDeep([...state.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, columns };
};

const toggleCreateReducer = (state: ItineraryTypeState): ItineraryTypeState => ({
    ...state, create: !state.create
});

const toggleExportReducer = (state: ItineraryTypeState, action: PayloadAction<boolean>): ItineraryTypeState => ({
    ...state, export_list: action.payload
});

const toggleExportFullReducer = (state: ItineraryTypeState): ItineraryTypeState => ({
    ...state, export_full: !state.export_full
});

const toggleFiltersReducer = (state: ItineraryTypeState): ItineraryTypeState => ({
    ...state, filter_open: !state.filter_open
});

const toggleItineraryTypeWithProdutsReducer = (state: ItineraryTypeState): ItineraryTypeState => ({
    ...state, itinerary_type_with_products: !state.itinerary_type_with_products
});

const toggleRefreshReducer = (state: ItineraryTypeState): ItineraryTypeState => ({
    ...state, refresh: !state.refresh
});

const updateBudgetReducer = (state: ItineraryTypeState, action: PayloadAction<string | number>): ItineraryTypeState => ({
    ...state, budget: action.payload
});

const updateColumnsReducer = (state: ItineraryTypeState, action: PayloadAction<Column[]>): ItineraryTypeState => ({
    ...state, columns: action.payload
});

const updateCurrencyReducer = (state: ItineraryTypeState, action: PayloadAction<null | Currency>): ItineraryTypeState => ({
    ...state,
    currency: action.payload,
    currency_error: ''
});

const updateDescriptionReducer = (state: ItineraryTypeState, action: PayloadAction<string>): ItineraryTypeState => ({
    ...state, description: action.payload
});

const updateDestinationReducer = (state: ItineraryTypeState, action: PayloadAction<null | DestinationReduced>): ItineraryTypeState => ({
    ...state,
    destination: action.payload,
    destination_error: ''
});

const updateCategoriesReducer = (state: ItineraryTypeState, action: PayloadAction<CircuitCategory[]>): ItineraryTypeState => ({
    ...state,
    categories: action.payload
});

const updateEndDateReducer = (state: ItineraryTypeState, action: PayloadAction<null | Date>): ItineraryTypeState => ({
    ...state, end_date: action.payload, duration_error: ''
});

const updateFilterCreatedDateGteReducer = (state: ItineraryTypeState, action: PayloadAction<null | Date>): ItineraryTypeState => ({
    ...state, filter_created_date_gte: action.payload
});

const updateFilterCreatedDateLteReducer = (state: ItineraryTypeState, action: PayloadAction<null | Date>): ItineraryTypeState => ({
    ...state, filter_created_date_lte: action.payload
});

const updateFilterCreatorReducer = (state: ItineraryTypeState, action: PayloadAction<string>): ItineraryTypeState => ({
    ...state, filter_creator: action.payload
});

const updateFilterDestinationReducer = (state: ItineraryTypeState, action: PayloadAction<null | DestinationReduced>): ItineraryTypeState => ({
    ...state, filter_destination: action.payload
});

const updateCategoriesFilterReducer = (state: ItineraryTypeState, action: PayloadAction<CircuitCategory[]>): ItineraryTypeState => ({
    ...state,
    filter_categories: action.payload
});

const updateFilterLastAuthorReducer = (state: ItineraryTypeState, action: PayloadAction<string>): ItineraryTypeState => ({
    ...state, filter_last_author: action.payload
});

const updateFilterModifiedDateGteReducer = (state: ItineraryTypeState, action: PayloadAction<null | Date>): ItineraryTypeState => ({
    ...state, filter_modified_date_gte: action.payload
});

const updateFilterModifiedDateLteReducer = (state: ItineraryTypeState, action: PayloadAction<null | Date>): ItineraryTypeState => ({
    ...state, filter_modified_date_lte: action.payload
});


const updateFilterPeriodReducer = (state: ItineraryTypeState, action: PayloadAction<'CREATION' | 'MODIFICATION'>): ItineraryTypeState => ({
    ...state,
    filter_period: action.payload,
    filter_period_type: 0,
    filter_created_date_gte: action.payload === 'MODIFICATION' ? null : state.filter_created_date_gte,
    filter_created_date_lte: action.payload === 'MODIFICATION' ? null : state.filter_created_date_lte,
    filter_modified_date_gte: action.payload === 'CREATION' ? null : state.filter_modified_date_gte,
    filter_modified_date_lte: action.payload === 'CREATION' ? null : state.filter_modified_date_lte,
});

const updateFilterPeriodTypeReducer = (state: ItineraryTypeState, action: PayloadAction<number>): ItineraryTypeState => ({
    ...state, filter_period_type: action.payload
});

const updateFilterVisibilityReducer = (state: ItineraryTypeState, action: PayloadAction<'PRIVATE' | 'PRIVATE_TO' | 'PUBLIC' | null>): ItineraryTypeState => ({
    ...state, filter_visibility: action.payload
});

const updateItineraryTypeDuplicateReducer = (state: ItineraryTypeState, action: PayloadAction<{ name: string, index: number }>): ItineraryTypeState => ({
    ...state, itinerary_type_duplicate: state.itinerary_type_duplicate.map((itinerary_type, index) => index === action.payload.index ? { ...itinerary_type, name: action.payload.name } : itinerary_type)
});

const updateListItemVisibilityReducer = (state: ItineraryTypeState, action: PayloadAction<ItineraryType>): ItineraryTypeState => ({
    ...state, list: state.list ? state.list?.map(itinerary => itinerary.id === action.payload.id ? { ...itinerary, visibility: action.payload.visibility } : itinerary) : null
});

const updateNameReducer = (state: ItineraryTypeState, action: PayloadAction<string>): ItineraryTypeState => ({
    ...state, name: action.payload, name_error: ''
});
const updateVideoLinkReducer = (state: ItineraryTypeState, action: PayloadAction<string>): ItineraryTypeState => ({
    ...state, video_link: action.payload
});

const updateStartDateReducer = (state: ItineraryTypeState, action: PayloadAction<Date | null>): ItineraryTypeState => ({
    ...state, start_date: action.payload, start_date_error: ''
});

const setColumnsReducer = (state: ItineraryTypeState, action: PayloadAction<Column[]>): ItineraryTypeState => ({
	...state, columns: action.payload
});

export const itineraryTypeSlice = createSlice({
	name: 'itineraryType',
	initialState,
	reducers: {
		initPictures: initPicturesReducer,
		addItineraryTypeDuplicate: addItineraryTypeDuplicateReducer,
		addAdultTravelers: addAdultTravelersReducer,
		addChildTravelers: addChildTravelersReducer,
		addPreferences: addPreferencesReducer,
		addRoomForTravelers: addRoomForTravelersReducer,
		deleteAdultTravelers: deleteAdultTravelersReducer,
		deleteChildTravelers: deleteChildTravelersReducer,
		deletePicture: deletePictureReducer,
		deletePictures: deletePicturesReducer,
		deleteRoomForTravelers: deleteRoomForTravelersReducer,
		moveDownColumn: moveDownColumnReducer,
		moveUpColumn: moveUpColumnReducer,
		resetFilters: resetFiltersReducer,
		resetItineraryType: resetItineraryTypeReducer,
		reseteItinerareyTypeDuplicate: reseteItineraryTypeDuplicateReducer,
		setEdit: setEditReducer,
		setExportType: setExportTypeReducer,
		setErrors: setErrorsReducer,
		setItineraryType: setItineraryTypeReducer,
		setItineraryTypePreferences: setItineraryTypePreferencesReducer,
		setLimit: setLimitReducer,
		setList: setListReducer,
		setOrderBy: setOrderByReducer,
		setPage: setPageReducer,
		setRunning: setRunningReducer,
		setSearch: setSearchReducer,
		toggleColumn: toggleColumnReducer,
		toggleCreate: toggleCreateReducer,
		toggleExport: toggleExportReducer,
		toggleExportFull: toggleExportFullReducer,
		toggleFilters: toggleFiltersReducer,
		toggleItineraryTypeWithProduts: toggleItineraryTypeWithProdutsReducer,
		toggleRefresh: toggleRefreshReducer,
		updateBudget: updateBudgetReducer,
		updateChildTravelers: updateChildTravelersReducer,
		updateChildTravelersError: updateChildTravelersErrorReducer,
		updateColumns: updateColumnsReducer,
		updateCurrency: updateCurrencyReducer,
		updateDescription: updateDescriptionReducer,
		updateDestination: updateDestinationReducer,
		updateEndDate: updateEndDateReducer,
		updateFilterCreatedDateGte: updateFilterCreatedDateGteReducer,
		updateFilterCreatedDateLte: updateFilterCreatedDateLteReducer,
		updateFilterCreator: updateFilterCreatorReducer,
		updateFilterDestination: updateFilterDestinationReducer,
		updateFilterLastAuthor: updateFilterLastAuthorReducer,
		updateFilterModifiedDateGte: updateFilterModifiedDateGteReducer,
		updateFilterModifiedDateLte: updateFilterModifiedDateLteReducer,
		updateFilterPeriod: updateFilterPeriodReducer,
		updateFilterPeriodType: updateFilterPeriodTypeReducer,
		updateFilterVisibility: updateFilterVisibilityReducer,
		updateItineraryTypeDuplicate: updateItineraryTypeDuplicateReducer,
		updateListItemVisibility: updateListItemVisibilityReducer,
		updateName: updateNameReducer,
		updateVideoLink: updateVideoLinkReducer,
		updatePicturesOrder: updatePicturesOrderReducer,
		updateStartDate: updateStartDateReducer,
		setColumns: setColumnsReducer,
        updateCategoriesFilter: updateCategoriesFilterReducer,
        updateCategories: updateCategoriesReducer
	},
	extraReducers: (builder) => {
		builder
			.addCase(setPreferences, (state, action) => {
				const field_name: string[] = [];
				const columns: Column[] = [];
				const preferences: Preferences[] = [];
				let order_by = '-created_date';
				let order: 'asc' | 'desc' = 'asc';
				action.payload.map((preference: Preferences) => {
					if (preference.module === 'ITINERARY_TYPE') {
						if (!field_name.includes(preference.field_name)) {
							switch (preference.field_name) {
								case 'NAME':
									columns.push({ label: 'name', field_name: preference.field_name, checked: preference.active, order_by: 'name', fixed_width: false, last_updated: false });
									order_by = preference.ordering_asc !== null ? 'name' : order_by;
									break;
								case 'DESTINATION':
									if (domain === 'tripmci') {
										break;
									}
									columns.push({ label: 'destination', field_name: preference.field_name, checked: preference.active, order_by: 'current_version__destination__current_version__localization__name', fixed_width: false, last_updated: false });
									order_by = preference.ordering_asc !== null ? 'current_version__destination__current_version__localization__name' : order_by;
									break;
                                case 'VISIBILITY':
                                    columns.push({ label: 'online-to', field_name: preference.field_name, checked: preference.active, order_by: '', fixed_width: true, last_updated: false });
                                    break;
                                case 'AGENCY_VISIBILITY':
                                    columns.push({ label: 'online-b2b', field_name: preference.field_name, checked: preference.active, order_by: '', fixed_width: true, last_updated: false });
                                    break;
                                case 'CATEGORIES':
                                    columns.push({ label: 'categories', field_name: preference.field_name, checked: preference.active, order_by: '', fixed_width: false, last_updated: false });
                                    order_by = preference.ordering_asc !== null ? '' : order_by;
                                    break;
								case 'CREATED_DATE':
									columns.push({ label: 'created-date', field_name: preference.field_name, checked: preference.active, order_by: 'created_date', fixed_width: true, last_updated: false });
									order_by = preference.ordering_asc !== null ? 'created_date' : order_by;
									break;
								case 'CREATED_BY':
									columns.push({ label: 'created-by', field_name: preference.field_name, checked: preference.active, order_by: 'creator__username', fixed_width: false, last_updated: false });
									order_by = preference.ordering_asc !== null ? 'creator__username' : order_by;
									break;
								case 'MODIFIED_DATE':
									columns.push({ label: 'modified-date', field_name: preference.field_name, checked: preference.active, order_by: 'modified_date', fixed_width: true, last_updated: false });
									order_by = preference.ordering_asc !== null ? 'modified_date' : order_by;
									break;
								case 'MODIFIED_BY':
									columns.push({ label: 'modified-by', field_name: preference.field_name, checked: preference.active, order_by: 'last_author__username', fixed_width: false, last_updated: false });
									order_by = preference.ordering_asc !== null ? 'last_author__username' : order_by;
									break;
								default:
									break;
							}
							preferences.push(preference);
							field_name.push(preference.field_name);
							order = preference.ordering_asc !== null ? preference.ordering_asc ? 'asc' : 'desc' : order;
						}
					}
				});
				if (columns.length !== state.columns.length) {
					state.columns.map(column => {
						let added = false;
						for (let i = 0; i < columns.length; i++) {
							if (columns[i].label === column.label) {
								added = true;
								break;
							}
						}
						if (!added) {
							columns.push({ ...column });
						}
					});
				}
				state.preferences = preferences;
				state.columns = columns;
				state.order_by = order_by;
				state.order = order;
			})
			.addCase(addPictures, addPicturesReducer)
			.addCase(setPictures, setPicturesReducer)
			.addDefaultCase(() => { })
	}

});

export const {
	initPictures,
	addItineraryTypeDuplicate,
	addAdultTravelers,
	addChildTravelers,
	addPreferences,
	addRoomForTravelers,
	deleteAdultTravelers,
	deleteChildTravelers,
	deletePicture,
	deletePictures,
	deleteRoomForTravelers,
	moveDownColumn,
	moveUpColumn,
	resetFilters,
	resetItineraryType,
	reseteItinerareyTypeDuplicate,
	setEdit,
	setExportType,
	setErrors,
	setItineraryType,
	setItineraryTypePreferences,
	setLimit,
	setList,
	setOrderBy,
	setPage,
	setRunning,
	setSearch,
	toggleCreate,
	toggleColumn,
	toggleExport,
	toggleExportFull,
	toggleItineraryTypeWithProduts,
	toggleFilters,
	toggleRefresh,
	updateBudget,
	updateChildTravelers,
	updateChildTravelersError,
	updateColumns,
	updateCurrency,
	updateDescription,
	updateDestination,
	updateEndDate,
	updateFilterCreatedDateGte,
	updateFilterCreatedDateLte,
	updateFilterCreator,
	updateFilterDestination,
	updateFilterLastAuthor,
	updateFilterModifiedDateGte,
	updateFilterModifiedDateLte,
	updateFilterPeriod,
	updateFilterPeriodType,
	updateFilterVisibility,
	updateItineraryTypeDuplicate,
	updateListItemVisibility,
	updateName,
	updateVideoLink,
	updatePicturesOrder,
	updateStartDate,
	setColumns,
    updateCategoriesFilter,
    updateCategories
} = itineraryTypeSlice.actions;

export default itineraryTypeSlice.reducer;